// TermsAndConditions.js
import React from 'react';
import {
    Box,
    VStack,
    Heading,
    Text,
    Divider,
    List,
    ListItem,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';

const campaignImage = `https://${window.location.host}/images/share-image.png`;

const TermsAndConditions = () => {
    return (
        <Box bg="#f5f3ee" color="#0f393b" py={10} px={6}>
            <Helmet>
                <title>Terms and Conditions - PurelyGive</title>
                <meta
                    name="description"
                    content="Read the terms and conditions for using PurelyGive's platform. Understand the agreement between you and PurelyGive LLC."
                />
                <meta
                    name="keywords"
                    content="terms and conditions, user agreement, PurelyGive, policies, nonprofit fundraising"
                />
                <meta property="og:title" content="PurelyGive Terms and Conditions" />
                <meta
                    property="og:description"
                    content="Understand the terms and conditions for using PurelyGive's zero-fee fundraising platform."
                />
                <meta property="og:image" content={campaignImage} />
                <meta
                    property="og:url"
                    content={`https://${window.location.host}/terms-and-conditions`}
                />
            </Helmet>
            <VStack spacing={8} maxW="800px" mx="auto" align="stretch" textAlign="left">
                <Heading as="h1" size="2xl" color="#f08453" mb={4}>
                    Terms & Conditions
                </Heading>

                {/* Terms & Conditions */}
                <Box>
                    {/* Effective Date */}
                    <Text fontSize="lg" fontWeight="bold" mb={4}>
                        Effective Date: August 13th, 2024
                    </Text>
                    <Text fontSize="md" mb={4}>
                        These Terms and Conditions (“Terms”) govern your use of the PurelyGive platform, including services provided to non-profit organizations (“Non-profits”), individual donors (“Donors”), and administrators (“Administrators”). By accessing or using the PurelyGive platform, you agree to be bound by these Terms.
                    </Text>

                    <br /><Divider />

                    {/* Section 1 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        1. Introduction
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive LLC (“PurelyGive,” “we,” “us,” or “our”) operates a donation platform that exclusively serves verified non-profit organizations in the United States. Our platform facilitates fundraising by allowing Non-profits to create and manage donation campaigns, and for Donors to contribute to those campaigns securely.
                    </Text>

                    {/* Section 2 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        2. User Roles
                    </Heading>
                    <List spacing={2} pl={4} styleType="disc">
                        <ListItem><strong>Non-profits:</strong> Organizations registered and verified as non-profits in the United States.</ListItem>
                        <ListItem><strong>Donors:</strong> Individuals making donations to campaigns created by Non-profits.</ListItem>
                        <ListItem><strong>Administrators:</strong> Individuals responsible for managing the platform on behalf of PurelyGive.</ListItem>
                    </List>

                    {/* Section 3 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        3. Payment Processing
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive uses Stripe and Stripe Connect as its payment processor for credit, debit, ACH, and wallet transactions. By using the platform, Non-profits and Donors agree to payment processing through Stripe under Stripe&apos;s terms of service. All transactions are secure and compliant with applicable laws and regulations.
                    </Text>

                    {/* Section 4 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        4. Ownership of Content
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive remains at all times the exclusive owner of all right, title, and interest in and to all content, data, and materials provided on the platform. Non-profits and Donors are granted a limited, non-exclusive license to use the platform for its intended purposes, subject to these Terms.
                    </Text>

                    {/* Section 5 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        5. Confidential Information
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        Confidential information includes, but is not limited to, financial data, user account information, and non-public details about organizations. Both PurelyGive and its users agree to protect and keep confidential any such information disclosed during the use of the platform. The Receiving Party shall not make copies or reproductions of the Disclosing Party’s Confidential Information except as necessary for the purposes of this Agreement.
                    </Text>

                    {/* Section 6 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        6. Compliance with Laws
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive and its users agree to comply with all applicable laws and regulations. This includes, but is not limited to, regulations related to fundraising, data protection, and anti-money laundering. Non-profits are responsible for ensuring that their use of the platform complies with all relevant laws and that all donations are used for lawful purposes.
                    </Text>

                    {/* Section 7 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        7. Changes to Services
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive reserves the right to modify, suspend, or discontinue any part of the platform at any time without notice. Users will be notified of significant changes through the email address associated with their account or via a notice on the platform. Continued use of the platform after changes have been made constitutes acceptance of the revised Terms.
                    </Text>

                    {/* Section 8 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        8. Dispute Resolution
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        Any disputes arising from the use of the PurelyGive platform will be resolved through mediation or arbitration, as mutually agreed by the parties. The governing law for these Terms is the law of the State of North Carolina, and any legal proceedings will take place in Charlotte, North Carolina.
                    </Text>

                    {/* Section 9 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        9. Publicity
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive may use the names and logos of Non-profits for marketing purposes, including on our website and in promotional materials, with the Non-profit’s consent. Non-profits may withdraw their consent at any time by contacting us.
                    </Text>

                    {/* Section 10 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        10. Data Privacy
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive collects, uses, and discloses personal information in accordance with our Privacy Policy. By using the platform, users agree to the terms of our Privacy Policy.
                    </Text>

                    {/* Section 11 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        11. Termination and Suspension
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive reserves the right to suspend or terminate any user’s access to the platform if they violate these Terms or engage in any activity that is harmful to the platform or other users. Upon termination, users will no longer have access to their accounts, and any remaining funds in a Non-profit’s account will be subject to our withdrawal policies.
                    </Text>

                    {/* Section 12 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        12. Free Services
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive provides its services to Non-profits free of charge. There are no paid services or fees associated with using the platform for Non-profits.
                    </Text>

                    {/* Section 13 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        13. User Conduct
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        Users agree to use the platform only for lawful purposes and in a manner that does not infringe on the rights of others. Any misuse of the platform, including but not limited to fraud, spamming, or unauthorized access to accounts, may result in immediate suspension or termination of access.
                    </Text>

                    {/* Section 14 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        14. Governing Law
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        These Terms are governed by and construed in accordance with the laws of the State of North Carolina. Any disputes or legal actions arising out of or in connection with these Terms will be brought exclusively in the courts of Charlotte, North Carolina.
                    </Text>

                    {/* Section 15 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        15. Force Majeure
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        Neither party shall be liable for any delay or failure to perform hereunder due to causes beyond its reasonable control, including, but not limited to, acts of God, natural disasters, acts of government, war, civil disobedience, cyber-attacks, or other similar events. Such delays will result in an extension of the corresponding obligation for a period equal to the time lost due to the event.
                    </Text>

                    {/* Section 16 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        16. Data Security
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        We implement appropriate technical and organizational measures to protect your personal information from unauthorized access, loss, or misuse. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
                    </Text>

                    {/* Section 17 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        17. International Use
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        If PurelyGive’s services are accessed outside of the United States, users agree to comply with all local laws and regulations, including data protection laws that may differ from those in the United States. PurelyGive is not responsible for ensuring compliance with laws outside the U.S.
                    </Text>

                    {/* Section 18 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        18. Amendments
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        PurelyGive reserves the right to modify these Terms at any time. Any changes will be effective immediately upon posting on the platform. Users will be notified of significant changes via the platform or email. Continued use of the platform after changes have been made constitutes acceptance of the revised Terms.
                    </Text>

                    {/* Section 19 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        19. Limitation of Liability
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        To the fullest extent permitted by law, PurelyGive shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill, arising out of or related to your use of the platform, whether based on contract, tort, strict liability, or other legal theory, even if PurelyGive has been advised of the possibility of such damages. In no event shall PurelyGive’s total liability to you for all claims exceed the amount of fees paid by you to PurelyGive, if any, in the twelve (12) months preceding the claim.
                    </Text>

                    {/* Section 20 */}
                    <Heading as="h2" size="lg" mt={4} mb={2}>
                        20. Contact Information
                    </Heading>
                    <Text fontSize="md" mb={4}>
                        For any questions or concerns about these Terms, please contact us at:
                    </Text>
                    <Text fontSize="md" mt={2}>
                        PurelyGive LLC
                        <br />
                        1024 Magna Lane
                        <br />
                        Indian Trail, NC 28079
                        <br />
                        <a href="mailto:support@purelygive.com">support@purelygive.com</a>
                        <br />
                        704-448-3121
                    </Text>

                    <br /><Divider />

                    <Text fontSize="md" mt={4}>
                        By using the PurelyGive platform, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                    </Text>
                </Box>
            </VStack>
        </Box>
    );
};

export default TermsAndConditions;
